import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/api'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const navigate = useNavigate()

  // Check if user is logged in on mount
  useEffect(() => {
    let isMounted = true

    const checkAuth = async () => {
      try {
        const response = await api.get('/api/snagmatic/auth/me')
        if (isMounted) {
          setUser(response.data)
          setError(null)
        }
      } catch (err) {
        if (isMounted) {
          setUser(null)
          setError(null) // Not an error, just not authenticated
        }
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    checkAuth()

    // Timeout: if auth check takes too long, stop loading anyway
    const timeout = setTimeout(() => {
      if (isMounted) {
        setIsLoading(false)
      }
    }, 5000)

    return () => {
      isMounted = false
      clearTimeout(timeout)
    }
  }, [])

  const login = useCallback(async (email, password) => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await api.post('/api/snagmatic/auth/login', { email, password })
      setUser(response.data.user)
      return response.data.user
    } catch (err) {
      const message = err.response?.data?.error || 'Login failed. Please try again.'
      setError(message)
      throw new Error(message)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const signup = useCallback(async (email, password, name) => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await api.post('/api/snagmatic/auth/signup', { email, password, name })
      setUser(response.data.user)
      return response.data.user
    } catch (err) {
      const message = err.response?.data?.error || 'Signup failed. Please try again.'
      setError(message)
      throw new Error(message)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const logout = useCallback(async () => {
    setIsLoading(true)
    try {
      await api.post('/api/snagmatic/auth/logout')
      setUser(null)
      setError(null)
      navigate('/login')
    } catch (err) {
      console.error('Logout error:', err)
      // Still clear user even if logout request fails
      setUser(null)
      navigate('/login')
    } finally {
      setIsLoading(false)
    }
  }, [navigate])

  const value = {
    user,
    isLoading,
    error,
    isAuthenticated: !!user,
    login,
    signup,
    logout,
    setUser,
    setError,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}
